import { useEffect, useMemo, useState, useTransition } from 'react';
import { RouterProvider } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { hasAuthParams, useAuth } from 'react-oidc-context';
import {
  userClaimsFetched,
  userLazyClaims,
  userLoaded,
  userRefetchClaims,
  userRefetchNav,
  userUnloaded
} from '@/redux/slices/authSlice';
import { useGetWarmUpSearchUserCacheQuery } from '@/redux/api/client/userSession';
import Router from './routes/Router';
import { usePLIPromoCookieExternalOrigin } from './hooks/usePLIPromoCookie';
import { RootState, store } from './redux/store';
import { resetUserData, handleLogout } from './redux/api/invalidateUserDataQueries';
import Awaiter from './components/ui/Awaiter/Awaiter';
import { useEventHubDataLayerConsumer } from './analytics/hooks/useEventHubDataLayerConsumer';
import { trackSetUserInfoEvent } from './analytics/user';
import {
  useGetUserContractStatusesQuery,
  useGetUserTrackingProfileQuery
} from './redux/api/client/userProfile';
import { skipToken } from '@reduxjs/toolkit/query';
import { useSignalR } from './hooks/useSignalR';
import useCreditPlanFeedbackSurvey from '@/components/ui/CreditPlan/hooks/useCreditPlanFeedbackSurvey';

window.env = {
  IS_AUTHENTICATED: 'false',
  FINISH_PROFILE_BLOCK_COOKIE_NAME: '',
  FIND_A_PROGRAM_BLOCK_COOKIE_NAME: '',
  YOU_CAN_FOLLOW_PRACTICE_AREAS: '',
  YOU_CAN_FOLLOW_INDUSTRIES: '',
  YOU_CAN_FOLLOW_FACULTIES: '',
  BLOCKED_ORGANIZATIONS: ''
};

const App = () => {
  // Application Insights page tracking is now handled by EnhancedRouteTracker

  const authState = useSelector((state: RootState) => state.auth);
  const auth = useAuth();
  const dispatch = useDispatch();
  const router = Router();
  useSignalR();
  const [isWarm, setIsWarm] = useState(false);
  const [hasCheckedConnect, setHasCheckedConnect] = useState(false);
  const [loggedInToConnect, setLoggedInToConnect] = useState(false);
  const [hasTriedSignin, setHasTriedSignin] = useState(false);
  const { processEvents } = useEventHubDataLayerConsumer();
  const [isHotjarIdentifySet, setIsHotjarIdentifySet] = useState(false);

  useCreditPlanFeedbackSurvey();

  usePLIPromoCookieExternalOrigin();

  const canLoadClaims = useMemo(() => {
    return (
      !authState.token ||
      !authState.ready ||
      !auth?.isAuthenticated ||
      hasAuthParams() ||
      auth.isLoading ||
      !!auth.activeNavigator
    );
  }, [
    auth.activeNavigator,
    auth?.isAuthenticated,
    auth.isLoading,
    authState.ready,
    authState.token
  ]);

  const [isPending, startTransition] = useTransition();
  const {
    refetch: lazyClaimsRefetch,
    isFetching: lazyClaimsFetching,
    isLoading: lazyClaimsLoading,
    isError: lazyClaimsError,
    data: lazyClaims
  } = useGetUserContractStatusesQuery(canLoadClaims ? skipToken : undefined);

  useEffect(() => {
    if (
      !lazyClaimsFetching &&
      !lazyClaimsLoading &&
      !!lazyClaims &&
      !lazyClaimsError &&
      !isPending &&
      !authState.claimFetched
    ) {
      startTransition(() => {
        dispatch(
          userLazyClaims({
            isIm: lazyClaims.isIM,
            isPm: lazyClaims.isPM
          })
        );
        dispatch(userClaimsFetched(true));
        console.info(
          'DIAG:User Claims loaded: ' + 'isPm:' + lazyClaims.isPM + ' isIm:' + lazyClaims.isIM
        );
      });
    }
  }, [
    authState.claimFetched,
    dispatch,
    isPending,
    lazyClaims,
    lazyClaimsError,
    lazyClaimsFetching,
    lazyClaimsLoading
  ]);

  useEffect(() => {
    if (authState.refetchClaims && (!lazyClaimsFetching || !lazyClaimsLoading)) {
      dispatch(userRefetchClaims(false));
      lazyClaimsRefetch();
      dispatch(userClaimsFetched(false));
      dispatch(userRefetchNav(true));
    }
  }, [authState.refetchClaims, dispatch, lazyClaimsFetching, lazyClaimsLoading, lazyClaimsRefetch]);

  const { data: trackinProfile, isSuccess: isSuccessTrackinProfile } =
    useGetUserTrackingProfileQuery(undefined, {
      skip: !authState.ready || !auth?.isAuthenticated
    });

  useEffect(() => {
    if (auth.isAuthenticated) {
      if (isSuccessTrackinProfile) {
        const persona =
          trackinProfile?.personas && trackinProfile?.personas.length > 0
            ? trackinProfile?.personas[0]
            : null;
        if (authState.isPm != undefined && authState.isIm != undefined) {
          trackSetUserInfoEvent(auth, !!authState.isPm, !!authState.isIm, persona);
        }
      }
    } else {
      if (!auth.isLoading) {
        trackSetUserInfoEvent(auth, false, false);
      }
    }
  }, [auth, authState.isIm, authState.isPm, isSuccessTrackinProfile, trackinProfile?.personas]);

  useEffect(() => {
    if (!hasAuthParams() && !auth.isLoading && !auth.activeNavigator) {
      if (auth.isAuthenticated) {
        setHasCheckedConnect(true);

        dispatch(
          userLoaded({
            token: auth.user?.access_token,
            name: auth.user?.profile.name,
            individualSk: auth.user?.profile.IndividualSK as string
          })
        );
        console.info(
          'DIAG: (app) User loaded: ' + auth.user?.profile.name + ' ' + auth.user?.access_token
        );
        if (!isWarm) {
          setIsWarm(true);
          resetUserData();
          (window as unknown as Record<string, unknown>).UserStore = auth.user?.profile;
          const e = new Event('UserStoreHasData');
          window.dispatchEvent(e);
        }
      } else {
        if (!auth?.user) {
          if (!hasCheckedConnect) {
            dispatch(userUnloaded());
            handleLogout();
            try {
              console.info('DIAG:Checking connect login ...');
              fetch(
                auth.settings.authority + '/isauthenticated?' + new Date().getTime().toString(),
                {
                  method: 'GET',
                  mode: 'cors',
                  cache: 'no-cache',
                  credentials: 'include',
                  headers: {
                    'Content-Type': 'application/json'
                  }
                }
              ).then(r => {
                if (r.ok) {
                  r.json().then(j => {
                    console.info('DIAG:Checking connect login ...', j);
                    setLoggedInToConnect(j?.isAuthenticated === true);
                    setHasCheckedConnect(true);
                  });
                } else setHasCheckedConnect(true);
              });
            } catch (error) {
              setHasCheckedConnect(true);
              console.error('DIAG:Checking connect login ...', error);
            }
          }

          if (loggedInToConnect && !hasTriedSignin) {
            setHasTriedSignin(true);
            console.info('DIAG:Trying connect signin ...');
            auth.signinSilent();
          }
        } else {
          setHasCheckedConnect(true);
          setIsWarm(false);
          dispatch(userUnloaded());
          handleLogout();
        }

        (window as unknown as Record<string, unknown>).UserStore = undefined;
        const e = new Event('UserStoreHasData');
        window.dispatchEvent(e);
      }
    }
    return () => {};
  }, [auth, dispatch, hasCheckedConnect, hasTriedSignin, isWarm, loggedInToConnect]);

  useEffect(() => {
    // the `return` is important - addAccessTokenExpiring() returns a cleanup function
    return auth.events.addAccessTokenExpired(() => {
      console.info('DIAG:Access token expired......');
      setHasTriedSignin(true);
      store.dispatch(userUnloaded());
      auth.signinSilent().then(data => {
        if (data) {
          //dispatch(userLoaded({ token: data.access_token, name: auth.user?.profile.name }));
          console.info('DIAG:Auth token renewed...');
        } else {
          console.info('DIAG:Refresh token invalid or expired...');
          auth?.removeUser().then(() => {
            console.info('DIAG:Attempt connect silent signin...');
            auth.signinSilent().then(data => {
              if (data) {
                console.info('DIAG:Silent signin success...');
                console.info('DIAG:Reloaded by app, expiration failed renew');
                router.navigate(0);
              } else {
                console.info('DIAG:Silent signin failed...');
              }
            });
          });
        }
      });
    });
  }, [auth, dispatch, router]);

  useEffect(() => {
    // the `return` is important - addAccessTokenExpiring() returns a cleanup function
    return auth.events.addUserUnloaded(() => {
      handleLogout();
      console.info('DIAG:You are signed out. Removing user');
      dispatch(userUnloaded());
    });
  }, [auth.events, dispatch]);

  useEffect(() => {
    // the `return` is important - addAccessTokenExpiring() returns a cleanup function
    return auth.events.addUserLoaded(user => {
      dispatch(
        userLoaded({
          token: user.access_token,
          name: user.profile.name,
          individualSk: user.profile?.IndividualSK as string
        })
      );
      console.info(
        'DIAG:You are signed in. Loading user: ' + user.profile.name + ' ' + user.access_token
      );
    });
  }, [auth.events, auth.user?.profile.IndividualSK, authState.isIm, authState.isPm, dispatch]);

  useEffect(() => {
    window.onbeforeunload = function () {
      processEvents();
    };

    return () => {
      window.onbeforeunload = null;
    };
  }, [processEvents]);

  useGetWarmUpSearchUserCacheQuery(false, {
    skip:
      !authState.token === undefined ||
      !authState.ready ||
      !auth?.isAuthenticated ||
      hasAuthParams() ||
      auth.isLoading ||
      auth.activeNavigator != undefined
  });

  useEffect(() => {
    if (
      window.hj &&
      !isHotjarIdentifySet &&
      auth.isAuthenticated &&
      isSuccessTrackinProfile &&
      !auth.isLoading &&
      auth.user?.profile
    ) {
      setIsHotjarIdentifySet(true);
      const isPM = !!authState.isPm;
      const isIM = !!authState.isIm;
      const organizationId = Number(auth.user?.profile.OrganizationSK);
      window.hj('identify', auth.user?.profile.sub ?? null, {
        membership_type: isIM ? 'IM' : isPM ? 'PM' : 'Retail',
        organization_id: !isNaN(organizationId) && organizationId > 0 ? organizationId : null,
        persona:
          trackinProfile?.personas && trackinProfile?.personas.length > 0
            ? trackinProfile?.personas[0]
            : null
      });
    }
  }, [
    auth.isAuthenticated,
    isSuccessTrackinProfile,
    auth.isLoading,
    auth.user?.profile,
    isHotjarIdentifySet,
    trackinProfile?.personas,
    authState.isPm,
    authState.isIm
  ]);

  //console.info(`DIAG:Render ${auth.activeNavigator} ${auth?.user?.expired} ${authState.ready} ${auth.isLoading} ${hasCheckedConnect}`);
  let _state: string = 'route';
  switch (auth.activeNavigator) {
    case 'signinRedirect':
      _state = 'redirect';
      break;
    case 'signoutRedirect':
      _state = 'redirect';
  }
  if (_state != 'redirect') {
    if (auth.activeNavigator == 'signinSilent' || auth?.user?.expired) {
      _state = 'loading';
    } else if (hasAuthParams()) {
      _state = 'signin';
    } else if (!authState.ready || auth.isLoading || !hasCheckedConnect) {
      _state = 'loading';
    }
  }

  if (_state === 'loading') {
    console.info('DIAG:Render loading');
    return <Awaiter content={['Loading...', 'Hang in there...', 'One moment...']} />;
  } else if (_state === 'signin') {
    console.info('DIAG:Render signin');
    return <Awaiter content={['Signing you in...', 'Hang in there...', 'One moment...']} />;
  } else if (_state === 'redirect') {
    console.info('DIAG:Render redirect');
    return <></>;
  } else {
    console.info('DIAG:Render router');
    return <RouterProvider router={router} />;
  }
};

export default App;
